/**
 * Time-of-arrival math: when does this storm reach that point.
 *
 * Lives in shared, with no dependencies, because both the worker (per-asset ETAs
 * on an evaluation) and the browser (the pin, measure and zone tools) must run the
 * *same* code. Two implementations would eventually disagree, and they would
 * disagree on the same screen.
 *
 * The geometry is deliberately hand-rolled rather than pulled from turf: turf is a
 * worker-only dependency, and shared is bundled into the browser.
 */
const METERS_PER_MILE = 1609.344;
const DEFAULT_SPEED_TOLERANCE = 0.2;
const DEFAULT_MAX_STALE_MIN = 30;
const DEFAULT_MIN_SPEED_MPH = 3;
const ETA_BUCKET_MIN = 5;
/** How far ahead the cone projects the track. */
const CONE_LOOKAHEAD_MIN = 60;
/** Floor, so a slow-moving storm still draws a cone you can see and grab. */
const MIN_CONE_MILES = 12;
// ---------------------------------------------------------------------------
// Local frame
// ---------------------------------------------------------------------------
/**
 * Equirectangular projection to meters, origin at `anchor`. Under 0.5% error at
 * county scale, which is an order of magnitude below the motion uncertainty this
 * whole module is built around — and it avoids a projection dependency.
 */
function projector(anchor) {
    const latRad = (anchor[1] * Math.PI) / 180;
    const mPerLon = Math.cos(latRad) * 111_320;
    const mPerLat = 110_540;
    return (c) => [(c[0] - anchor[0]) * mPerLon, (c[1] - anchor[1]) * mPerLat];
}
function unproject(anchor) {
    const latRad = (anchor[1] * Math.PI) / 180;
    const mPerLon = Math.cos(latRad) * 111_320;
    const mPerLat = 110_540;
    return (p) => [anchor[0] + p[0] / mPerLon, anchor[1] + p[1] / mPerLat];
}
/** Great-circle distance. Used for the measure tool and for straight-line readouts. */
export function haversineMiles(a, b) {
    const R = 3958.7613;
    const toRad = Math.PI / 180;
    const dLat = (b[1] - a[1]) * toRad;
    const dLon = (b[0] - a[0]) * toRad;
    const lat1 = a[1] * toRad;
    const lat2 = b[1] * toRad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Motion bearing (toward, compass degrees) -> unit vector in the local frame. */
function unitVector(bearingDeg) {
    const rad = (bearingDeg * Math.PI) / 180;
    return [Math.sin(rad), Math.cos(rad)]; // 0deg = +y (north), 90deg = +x (east)
}
/** Every ring of a Polygon or MultiPolygon, projected into the local frame. */
function projectRings(geometry, project) {
    const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    const out = [];
    for (const poly of polys) {
        if (!Array.isArray(poly))
            continue;
        for (const ring of poly) {
            if (!Array.isArray(ring) || ring.length < 3)
                continue;
            const projected = [];
            for (const pt of ring) {
                if (!Array.isArray(pt) || typeof pt[0] !== 'number' || typeof pt[1] !== 'number')
                    continue;
                projected.push(project([pt[0], pt[1]]));
            }
            if (projected.length >= 3)
                out.push(projected);
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
/** Ray casting. Even crossings = outside. Rings include holes, so parity is correct. */
function originInsideRings(rings) {
    let inside = false;
    for (const ring of rings) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [xi, yi] = ring[i];
            const [xj, yj] = ring[j];
            if (yi > 0 !== yj > 0 && 0 < ((xj - xi) * (0 - yi)) / (yj - yi) + xi)
                inside = !inside;
        }
    }
    return inside;
}
/**
 * Parameters `s > 0` at which the ray from the origin along `d` crosses a ring edge.
 *
 * This is the heart of the whole module. The point `p` is struck at time `t` iff
 * `p - u*v*t` lies inside the polygon — that is, iff walking *backwards* along the
 * motion vector from `p` runs into the weather. So we cast backwards and read off
 * the crossings. Casting toward the nearest edge instead would be wrong: the
 * nearest edge is often off to one side and never sweeps over the point at all.
 */
function rayCrossings(rings, d) {
    const hits = [];
    for (const ring of rings) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const a = ring[j];
            const b = ring[i];
            const ex = b[0] - a[0];
            const ey = b[1] - a[1];
            const denom = d[0] * ey - d[1] * ex;
            if (Math.abs(denom) < 1e-9)
                continue; // parallel
            // Solve  s*d = a + t*e  for s (along ray) and t (along edge).
            const s = (a[0] * ey - a[1] * ex) / denom;
            const t = (a[0] * d[1] - a[1] * d[0]) / denom;
            if (s > 0 && t >= 0 && t <= 1)
                hits.push(s);
        }
    }
    return hits.sort((p, q) => p - q);
}
function pointToSegmentMeters(p, a, b) {
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const wx = p[0] - a[0];
    const wy = p[1] - a[1];
    const len2 = vx * vx + vy * vy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (wx * vx + wy * vy) / len2));
    const dx = wx - t * vx;
    const dy = wy - t * vy;
    return Math.hypot(dx, dy);
}
/**
 * Miles from a point to the nearest part of an open polyline.
 *
 * Exported because road closures need it on BOTH sides: the worker decides whether a
 * closure is near an asset, and the browser prints the same number next to it. A second
 * implementation — turf on the server, this in the browser — would eventually disagree
 * by a few hundred feet, and it would disagree on the same screen. See the note at the
 * top of this file.
 *
 * The frame is anchored at `point`, so the equirectangular error where the measurement
 * is taken is zero, and grows only along the road away from it.
 */
export function pointToPathMiles(point, path) {
    if (path.length === 0)
        return Infinity;
    // A one-vertex "path" is a point; great-circle is both cheaper and exact.
    if (path.length === 1)
        return haversineMiles(point, path[0]);
    const project = projector(point);
    const pts = path.map(project);
    let best = Infinity;
    for (let i = 1; i < pts.length; i += 1) {
        const d = pointToSegmentMeters([0, 0], pts[i - 1], pts[i]);
        if (d < best)
            best = d;
    }
    return best / METERS_PER_MILE;
}
/** Straight-line distance from the origin to the nearest ring edge, in meters. */
function distanceToRingsMeters(rings) {
    let best = Infinity;
    for (const ring of rings) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const d = pointToSegmentMeters([0, 0], ring[j], ring[i]);
            if (d < best)
                best = d;
        }
    }
    return Number.isFinite(best) ? best : 0;
}
/**
 * One deterministic pass: no uncertainty, no refusals. `etaToPoint` runs this three
 * times with perturbed motion to produce a range.
 */
function rawEta(rings, bearingDeg, speedMph, ageMinutes) {
    const u = unitVector(bearingDeg);
    // The vector was measured `ageMinutes` ago; the weather has moved on since.
    // Shift the polygon forward rather than discounting the answer afterwards.
    const advanceM = (speedMph * (ageMinutes / 60)) * METERS_PER_MILE;
    const moved = advanceM === 0
        ? rings
        : rings.map((ring) => ring.map((p) => [p[0] + u[0] * advanceM, p[1] + u[1] * advanceM]));
    const distanceMiles = distanceToRingsMeters(moved) / METERS_PER_MILE;
    const metersPerMinute = (speedMph * METERS_PER_MILE) / 60;
    if (originInsideRings(moved)) {
        const forward = rayCrossings(moved, u);
        return {
            status: 'inside',
            minutes: 0,
            clearsInMinutes: forward.length ? forward[forward.length - 1] / metersPerMinute : null,
            distanceMiles: 0,
        };
    }
    const backward = rayCrossings(moved, [-u[0], -u[1]]);
    if (!backward.length) {
        // Nothing upwind of us: the swept corridor never covers this point.
        return { status: 'moving-away', minutes: null, clearsInMinutes: null, distanceMiles };
    }
    return {
        status: 'arriving',
        minutes: backward[0] / metersPerMinute,
        clearsInMinutes: backward[backward.length - 1] / metersPerMinute,
        distanceMiles,
    };
}
function ageMinutes(observedAt, now) {
    const t0 = new Date(observedAt).getTime();
    const t1 = new Date(now).getTime();
    if (!Number.isFinite(t0) || !Number.isFinite(t1))
        return 0;
    return Math.max(0, (t1 - t0) / 60_000);
}
function defaultBearingTolerance(confidence) {
    return confidence === 'low' ? 25 : 15;
}
/** One step down per 15 minutes of staleness, never above the source's own confidence. */
function degradeConfidence(base, age) {
    const steps = Math.floor(age / 15);
    const ladder = ['high', 'medium', 'low'];
    const start = ladder.indexOf(base);
    return ladder[Math.min(ladder.length - 1, start + steps)];
}
function refuse(reason, distanceMiles) {
    return {
        status: 'unknown',
        minutes: null,
        minutesLow: null,
        minutesHigh: null,
        distanceMiles,
        clearsInMinutes: null,
        confidence: 'low',
        cone: null,
        coneReachMiles: 0,
        reason,
    };
}
/**
 * The uncertainty cone: a wedge from the weather's current position out to the
 * point, spread by the bearing tolerance. Purely for drawing — it carries no
 * information the numbers don't, but it makes "this could miss you" legible.
 */
function buildCone(target, bearingDeg, toleranceDeg, reachMiles) {
    if (!(reachMiles > 0))
        return null;
    const toXY = unproject(target);
    const reachM = reachMiles * METERS_PER_MILE;
    const back = bearingDeg + 180;
    const ring = [toXY([0, 0])];
    for (let a = -toleranceDeg; a <= toleranceDeg + 1e-9; a += Math.max(1, toleranceDeg / 6)) {
        const u = unitVector(back + a);
        ring.push(toXY([u[0] * reachM, u[1] * reachM]));
    }
    ring.push(ring[0]);
    return { type: 'Polygon', coordinates: [ring] };
}
/**
 * When does `geometry`, moving per `motion`, reach `point`?
 *
 * Returns `status: 'unknown'` — with no number at all — rather than guessing when
 * the inputs cannot support an answer. That refusal is a feature: this is
 * safety-adjacent software, and a confident wrong minute count is worse than
 * "we don't know".
 */
export function etaToPoint(geometry, motion, point, now, opts = {}) {
    if (!geometry)
        return refuse('no-geometry', 0);
    if (!motion) {
        const rings = projectRings(geometry, projector(point));
        const d = originInsideRings(rings) ? 0 : distanceToRingsMeters(rings) / METERS_PER_MILE;
        return refuse('no-motion', d);
    }
    const project = projector(point);
    const rings = projectRings(geometry, project);
    if (!rings.length)
        return refuse('no-geometry', 0);
    const straightMiles = originInsideRings(rings) ? 0 : distanceToRingsMeters(rings) / METERS_PER_MILE;
    const age = ageMinutes(motion.observedAt, now);
    const maxStale = opts.maxStaleMinutes ?? DEFAULT_MAX_STALE_MIN;
    const minSpeed = opts.minSpeedMph ?? DEFAULT_MIN_SPEED_MPH;
    if (age > maxStale)
        return refuse('stale-motion', straightMiles);
    if (!(motion.speedMph >= minSpeed))
        return refuse('zero-speed', straightMiles);
    const speedTol = opts.speedTolerance ?? DEFAULT_SPEED_TOLERANCE;
    const bearingTol = opts.bearingToleranceDeg ?? defaultBearingTolerance(motion.confidence);
    const confidence = degradeConfidence(motion.confidence, age);
    const nominal = rawEta(rings, motion.bearingDeg, motion.speedMph, age);
    const distanceMiles = Math.round(nominal.distanceMiles * 100) / 100;
    if (nominal.status === 'inside') {
        return {
            status: 'inside',
            minutes: 0,
            minutesLow: 0,
            minutesHigh: 0,
            distanceMiles: 0,
            clearsInMinutes: nominal.clearsInMinutes === null ? null : Math.round(nominal.clearsInMinutes),
            confidence,
            cone: null,
            coneReachMiles: 0,
        };
    }
    // Speed and bearing uncertainty answer two different questions, so they are
    // perturbed separately.
    //
    // Speed sets the *timing* envelope: same track, 20% faster or slower.
    const speedVariants = [
        rawEta(rings, motion.bearingDeg, motion.speedMph * (1 + speedTol), age),
        rawEta(rings, motion.bearingDeg, motion.speedMph * (1 - speedTol), age),
    ];
    // Bearing answers whether it arrives at all. Combining the two naively hides this:
    // a bearing-shifted track often misses the polygon outright, and a miss carries no
    // arrival time, so it silently drops out of the envelope and *narrows* the range at
    // exactly the moment the answer is least certain. Track them on their own.
    const bearingVariants = [
        rawEta(rings, motion.bearingDeg - bearingTol, motion.speedMph, age),
        rawEta(rings, motion.bearingDeg + bearingTol, motion.speedMph, age),
    ];
    const mayMiss = bearingVariants.some((v) => v.status === 'moving-away');
    const arrivals = [nominal, ...speedVariants, ...bearingVariants]
        .filter((v) => v.status === 'arriving' && v.minutes !== null)
        .map((v) => v.minutes);
    if (nominal.status === 'moving-away' || !arrivals.length) {
        return {
            status: 'moving-away',
            minutes: null,
            minutesLow: null,
            minutesHigh: null,
            distanceMiles,
            clearsInMinutes: null,
            confidence,
            cone: null,
            coneReachMiles: 0,
        };
    }
    const low = Math.min(...arrivals);
    const high = Math.max(...arrivals);
    /*
     * The cone showed the gap between here and the weather, so it shrank to nothing as the
     * storm closed in — smallest at the moment it matters most. It is a track corridor, not
     * a measuring stick: reach far enough to be read at a glance, and at least as far as the
     * storm travels in the next hour.
     */
    const coneReach = Math.max(distanceMiles, motion.speedMph * (CONE_LOOKAHEAD_MIN / 60), MIN_CONE_MILES);
    const minutesLow = Math.max(0, Math.floor(low / ETA_BUCKET_MIN) * ETA_BUCKET_MIN);
    let minutesHigh = Math.ceil(high / ETA_BUCKET_MIN) * ETA_BUCKET_MIN;
    if (minutesHigh <= minutesLow)
        minutesHigh = minutesLow + ETA_BUCKET_MIN;
    return {
        status: 'arriving',
        minutes: Math.round(nominal.minutes),
        minutesLow,
        minutesHigh,
        distanceMiles,
        clearsInMinutes: nominal.clearsInMinutes === null ? null : Math.round(nominal.clearsInMinutes),
        confidence,
        mayMiss,
        cone: buildCone(point, motion.bearingDeg, bearingTol, coneReach),
        coneReachMiles: coneReach,
    };
}
/**
 * Measure tool: the length of a line, and how long the weather takes to cross it.
 * Crossing time is the line's extent *along the motion vector* — a line drawn
 * perpendicular to the storm's track is crossed instantly, however long it is.
 */
export function etaAlongLine(a, b, motion, _now) {
    const miles = haversineMiles(a, b);
    const km = Math.round(miles * 1.609344 * 10) / 10;
    if (!motion || !(motion.speedMph >= DEFAULT_MIN_SPEED_MPH)) {
        return { miles: Math.round(miles * 10) / 10, km, crossingMinutes: null };
    }
    const project = projector(a);
    const pb = project(b);
    const u = unitVector(motion.bearingDeg);
    const alongMeters = Math.abs(pb[0] * u[0] + pb[1] * u[1]);
    const alongMiles = alongMeters / METERS_PER_MILE;
    return {
        miles: Math.round(miles * 10) / 10,
        km,
        crossingMinutes: Math.round((alongMiles / motion.speedMph) * 60),
    };
}
/**
 * Earliest arrival anywhere in a drawn zone. Sampled at the zone's vertices, which
 * is exact for convex zones and conservative enough for the hand-drawn ones.
 */
export function etaToZone(zone, motion, geometry, now, opts) {
    if (!zone)
        return refuse('no-geometry', 0);
    const samples = [];
    const polys = zone.type === 'Polygon' ? [zone.coordinates] : zone.coordinates;
    for (const poly of polys) {
        if (!Array.isArray(poly))
            continue;
        for (const ring of poly) {
            if (!Array.isArray(ring))
                continue;
            for (const pt of ring) {
                if (Array.isArray(pt) && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
                    samples.push([pt[0], pt[1]]);
                }
            }
        }
    }
    if (!samples.length)
        return refuse('no-geometry', 0);
    let best = null;
    for (const s of samples) {
        const r = etaToPoint(geometry, motion, s, now, opts);
        if (r.status === 'inside')
            return r;
        if (r.status !== 'arriving') {
            if (!best)
                best = r;
            continue;
        }
        if (!best || best.status !== 'arriving' || (r.minutesLow ?? Infinity) < (best.minutesLow ?? Infinity)) {
            best = r;
        }
    }
    return best ?? refuse('no-motion', 0);
}
// ---------------------------------------------------------------------------
// Formatting — the only place the numbers become words
// ---------------------------------------------------------------------------
export function formatDistance(miles) {
    if (!Number.isFinite(miles))
        return 'unknown distance';
    if (miles < 1)
        return 'under a mile';
    const n = Math.round(miles);
    return `${n} ${n === 1 ? 'mile' : 'miles'}`;
}
/**
 * The range, in words. Never the point estimate: "34 minutes" claims a precision
 * that does not exist, and a reader treats it as a promise.
 */
export function formatEtaRange(r) {
    if (r.status !== 'arriving' || r.minutesLow === null || r.minutesHigh === null)
        return null;
    if (r.minutesLow >= 90)
        return 'in more than an hour and a half';
    if (r.minutesLow === 0)
        return `in under ${r.minutesHigh} minutes`;
    return `in about ${r.minutesLow}–${r.minutesHigh} minutes`;
}
// ---------------------------------------------------------------------------
// Forward projection: where a storm is going, rather than when it gets here
// ---------------------------------------------------------------------------
/**
 * The ground a storm could cover over the next `leadMinutes`.
 *
 * The arrival cone answers "when does it reach me" and points backwards, upwind from a
 * spot you picked. This is the other question — "what is in its way" — so it starts at
 * the storm and opens forward along the track.
 *
 * It widens with the same bearing tolerance the arrival maths uses, because the
 * uncertainty is the same uncertainty: a track is only known to within a handful of
 * degrees, and that spread grows with distance. The near edge is the storm's own
 * leading edge, so the swath joins the polygon it came from rather than floating
 * detached from it.
 *
 * Returns null when there is nothing meaningful to draw — a stalled storm has no path.
 */
export function stormSwath(geometry, motion, leadMinutes, opts = {}) {
    if (!geometry || !motion)
        return null;
    if (!(leadMinutes > 0))
        return null;
    if (!(motion.speedMph >= (opts.minSpeedMph ?? DEFAULT_MIN_SPEED_MPH)))
        return null;
    // Anchor the local frame on the storm itself so the projection is measured from it.
    const anchor = geometryAnchor(geometry);
    if (!anchor)
        return null;
    const project = projector(anchor);
    const rings = projectRings(geometry, project);
    if (!rings.length)
        return null;
    const u = unitVector(motion.bearingDeg);
    // Perpendicular, pointing to the right of travel.
    const perp = [u[1], -u[0]];
    let alongMax = -Infinity;
    let sideMin = Infinity;
    let sideMax = -Infinity;
    for (const ring of rings) {
        for (const pt of ring) {
            const along = pt[0] * u[0] + pt[1] * u[1];
            const side = pt[0] * perp[0] + pt[1] * perp[1];
            if (along > alongMax)
                alongMax = along;
            if (side < sideMin)
                sideMin = side;
            if (side > sideMax)
                sideMax = side;
        }
    }
    if (!Number.isFinite(alongMax) || !Number.isFinite(sideMin) || !Number.isFinite(sideMax))
        return null;
    const reachM = (motion.speedMph * (leadMinutes / 60)) * METERS_PER_MILE;
    const tol = opts.bearingToleranceDeg ?? defaultBearingTolerance(motion.confidence);
    // How far the edges fan out sideways over that distance.
    const spreadM = reachM * Math.tan((tol * Math.PI) / 180);
    const toLngLat = unproject(anchor);
    const at = (along, side) => toLngLat([u[0] * along + perp[0] * side, u[1] * along + perp[1] * side]);
    // Near edge is the storm's own leading edge; far edge is that edge fanned out.
    const ring = [
        at(alongMax, sideMin),
        at(alongMax, sideMax),
        at(alongMax + reachM, sideMax + spreadM),
        at(alongMax + reachM, sideMin - spreadM),
    ];
    ring.push(ring[0]);
    return { type: 'Polygon', coordinates: [ring] };
}
/** Mean of a geometry's vertices — enough of a centre for a local projection. */
function geometryAnchor(geometry) {
    const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const poly of polys) {
        if (!Array.isArray(poly))
            continue;
        for (const ring of poly) {
            if (!Array.isArray(ring))
                continue;
            for (const pt of ring) {
                if (Array.isArray(pt) && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
                    sx += pt[0];
                    sy += pt[1];
                    n += 1;
                }
            }
        }
    }
    return n ? [sx / n, sy / n] : null;
}
/**
 * The part of an echo footprint belonging to the storm at `point`.
 *
 * A nowcast covers a box about ninety miles across, so its geometry routinely holds two
 * or three unrelated storms. Projecting all of it takes the leading edge of the whole
 * footprint, which can sit on a different storm from the one that was tapped — the path
 * then appears over the wrong cell entirely. Keep only the pieces near the tap.
 */
export function echoNear(geometry, point, radiusMiles = 30) {
    if (!geometry)
        return null;
    const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    const kept = [];
    for (const poly of polys) {
        if (!Array.isArray(poly))
            continue;
        const ring = poly[0];
        if (!Array.isArray(ring) || !ring.length)
            continue;
        let near = false;
        for (const pt of ring) {
            if (!Array.isArray(pt) || typeof pt[0] !== 'number' || typeof pt[1] !== 'number')
                continue;
            if (haversineMiles(point, [pt[0], pt[1]]) <= radiusMiles) {
                near = true;
                break;
            }
        }
        if (near)
            kept.push(poly);
    }
    if (!kept.length)
        return null;
    return { type: 'MultiPolygon', coordinates: kept };
}
//# sourceMappingURL=eta.js.map